import React, { useState, useMemo } from 'react';
import { useStacklineTheme, SlMenu, SlDropdownMenuOption } from '@stackline/ui';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import IconButton from '@mui/material/IconButton';
import domtoimage from 'src/utils/dom-to-imagev2';
import saveAs from 'file-saver';
import { useSnackbar } from 'src/utils/Hooks/typedHooks';
import { defaultImageOption } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/hooks/useCsvExporter';

export interface BeaconChartWithLegendProps {
  title?: React.ReactNode;
  disableExportCsv?: boolean;
  convertSeriesToDelimitedData?: () => Promise<any>;
  buildDropdownMenuOptions?: (options: SlDropdownMenuOption[]) => SlDropdownMenuOption[];
  dropdownOptionHandler?: (option: SlDropdownMenuOption, defaultDropdownHandler: () => Promise<void>) => void;
  showExportMenu?: boolean;
  saveImageRef: React.RefObject<HTMLDivElement>;
}

/**
 * Generic container for charts with a legend. The chart to render should be passed in as a child.
 */
const DottedExports = ({
  disableExportCsv = false,
  convertSeriesToDelimitedData,
  buildDropdownMenuOptions,
  dropdownOptionHandler,
  title,
  /**
   * Ref to the container to save as an image
   */
  saveImageRef
}: BeaconChartWithLegendProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { showSnackbar } = useSnackbar();
  const handleOpenExports = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const theme = useStacklineTheme();
  const handleDropdownClick = (option: SlDropdownMenuOption) => {
    const defaultDropdownHandler = async () => {
      try {
        const file_name = (typeof title === 'string' ? title : null) || 'chart';
        window.focus();
        // Wait for focus and potential rendering delays
        await new Promise((resolve) => setTimeout(resolve, 200));

        const maxWaitTime = 2000;
        const pollInterval = 100;
        const startTime = Date.now();

        while (!saveImageRef.current && Date.now() - startTime < maxWaitTime) {
          await new Promise((resolve) => setTimeout(resolve, pollInterval));
        }

        // Wait for the ref to become available, with max waiting time of 2 seconds
        if (!saveImageRef.current) {
          console.error(`Chart container ref not available after ${maxWaitTime}ms`);
          showSnackbar({ type: 'error', message: 'Error copying image: Chart not ready.' });
          return;
        }

        // Ref is available now, proceed with copying
        const blob = (await domtoimage.toBlob(
          saveImageRef?.current,
          defaultImageOption
        )) as Blob;

        switch (option.id) {
          case 'saveImage': {
            if (blob) {
              saveAs(blob, `${file_name}.png`);
            } else {
              console.error(`Unable to generate blob for chart: ${title}`);
            }
            setAnchorEl(null);
            break;
          }
          case 'copyImage': {
            if (blob) {
              const clipboardItem = new ClipboardItem({ 'image/png': blob });
              await navigator.clipboard.write([clipboardItem]);
              showSnackbar({ type: 'success', message: 'Image copied to clipboard!' });
            } else {
              console.error(`Unable to generate blob for chart: ${title}`);
            }
            setAnchorEl(null);
            break;
          }
          case 'exportCsv': {
            if (convertSeriesToDelimitedData) {
              // Export as csv
              await convertSeriesToDelimitedData();
            }
            setAnchorEl(null);
            break;
          }

          default:
        }
      } catch (e) {
        console.error('Error exporting chart:', e);
      }
    };

    if (dropdownOptionHandler) {
      dropdownOptionHandler(option, defaultDropdownHandler);
    } else {
      defaultDropdownHandler();
    }
  };

  const handleCloseExports = () => {
    setAnchorEl(null);
  };

  const exportButton = (
    <IconButton
      onClick={handleOpenExports}
      sx={{
        height: theme.spacing.mdl,
        width: theme.spacing.mdl,
        borderRadius: theme.spacing.xs,
        color: theme.colors.primary
      }}
    >
      <MoreHorizIcon />
    </IconButton>
  );

  const dropdownOptions: SlDropdownMenuOption[] = useMemo(() => {
    const defaultOptions: SlDropdownMenuOption[] = [
      {
        id: 'saveImage',
        label: 'Save image'
      },
      {
        id: 'copyImage',
        label: 'Copy image'
      },
      ...(disableExportCsv
        ? []
        : [
            {
              id: 'exportCsv',
              label: 'Export CSV'
            }
          ])
    ];
    if (buildDropdownMenuOptions) {
      return buildDropdownMenuOptions(defaultOptions);
    }
    return defaultOptions;
  }, [disableExportCsv, buildDropdownMenuOptions]);

  return (
    <div style={{ backgroundColor: 'white' }}>
      {exportButton}
      <SlMenu
        anchorEl={anchorEl}
        open={!!anchorEl}
        selectedId={null}
        onChange={handleDropdownClick}
        onClose={handleCloseExports}
        options={dropdownOptions}
      />
    </div>
  );
};

export default DottedExports;
